// talkingAvatarJobStore — the studio's memory of "Doğal konuşan video" jobs.
//
// A job runs 10–30 minutes, so it must outlive the Avatar modal (closed,
// reopened, another scene picked) and a page reload. Records live here, in a
// module-level store read through useSyncExternalStore (same pattern as
// narrationDraftStore), mirrored to sessionStorage under
// "aidea:talkingAvatarJobs:<trainingId>" and pruned after 48 h.
//
// Updates arrive from three directions — the session socket (the worker's
// progress, the gateway's own inspect/failed events, session_job_completed) and
// HTTP polling — in any order and often TWICE. reduceJobEvent is the single,
// pure merge: completed / failed are sticky, so a duplicate or late event is a
// no-op; "inspect" counts as completed; a completion without a result asks the
// caller to fetch it (needsResult).
//
// Record: { jobId, trainingId, navId, sceneIndex, model, modelLabel, aspectRatio,
//   removeBackground, text, language, voice: { id, providerId, name, isPrivate },
//   avatar: { kind, thumbUrl, name }, aiJobId, status, progress, stage,
//   segment: { done, total }, result, error: { code, message }, approved,
//   applied, dismissed, settled, createdAt, updatedAt, lastChangeAt }
// status: "pending" | "processing" | "completed" | "failed" | "unknown"
import { useSyncExternalStore } from "react";

const STORAGE_PREFIX = "aidea:talkingAvatarJobs:";
const PREFS_KEY = "aidea:talkingAvatarPrefs";
const HOUR = 60 * 60 * 1000;
export const JOB_MAX_AGE_MS = 48 * HOUR;

const TERMINAL = new Set(["completed", "failed"]);
export const isTerminalStatus = (status) => TERMINAL.has(status);

const STATUS_MAP = {
  inspect: "completed",
  completed: "completed",
  complete: "completed",
  done: "completed",
  success: "completed",
  approved: "completed",
  failed: "failed",
  error: "failed",
  pending: "pending",
  queued: "pending",
  waiting: "pending",
  delayed: "pending",
  created: "pending",
  processing: "processing",
  active: "processing",
  running: "processing",
  "in-progress": "processing",
  in_progress: "processing",
  unknown: "unknown",
};

const isNum = (v) => typeof v === "number" && Number.isFinite(v);

function toSegment(seg) {
  if (!seg || typeof seg !== "object") return undefined;
  const done = Number(seg.done);
  const total = Number(seg.total);
  if (!Number.isFinite(done) || !Number.isFinite(total)) return undefined;
  return { done, total };
}

// Failure code: metadata.error.code → error.code → the error string itself.
function extractError(d) {
  const md =
    d.metadata && d.metadata.error && typeof d.metadata.error === "object"
      ? d.metadata.error
      : null;
  const eo = d.error && typeof d.error === "object" ? d.error : null;
  const es = typeof d.error === "string" && d.error.trim() ? d.error.trim() : null;
  const code = (md && md.code) || (eo && eo.code) || es || null;
  const message = (md && md.message) || (eo && eo.message) || es || null;
  if (!code && !message) return undefined;
  return { code: code || null, message: message || null };
}

// One shape for every source. kind: "update" (session_job_update),
// "completed" (session_job_completed) or "http" (GET /talking-avatar/:id).
// Socket payloads may come wrapped as { data } (c?.data ?? c precedent).
export function normalizeJobEvent(raw, kind = "update") {
  const top = raw && typeof raw === "object" ? raw : {};
  const d =
    kind !== "http" && top.data && typeof top.data === "object" && !(top.jobId || top.id)
      ? top.data
      : top;
  const md = d.metadata && typeof d.metadata === "object" ? d.metadata : {};
  const out = {};
  const jobId = d.jobId || d.id;
  if (jobId) out.jobId = jobId;
  const jobType = d.jobType || d.type;
  if (jobType) out.jobType = jobType;
  const status =
    kind === "completed" ? "completed" : STATUS_MAP[String(d.status || "").toLowerCase()];
  if (status) out.status = status;
  if (isNum(d.progress)) out.progress = d.progress;
  const stage = d.stage || md.stage;
  if (stage) out.stage = stage;
  const segment = toSegment(d.segment || md.segment);
  if (segment) out.segment = segment;
  // session_job_update sends result: "available" — a flag, not the payload.
  if (d.result && typeof d.result === "object") out.result = d.result;
  const error = extractError(d);
  if (error) out.error = error;
  const navId = d.navId || md.navId;
  if (navId) out.navId = navId;
  if (d.approved === true) out.approved = true;
  return out;
}

const sameValue = (a, b) => {
  if (a === b) return true;
  if (a && b && typeof a === "object" && typeof b === "object") {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch (e) {
      return false;
    }
  }
  return false;
};

const RUNNING = new Set(["pending", "processing", "unknown"]);

// Pure merge of one normalized event into a record → { job, changed, needsResult }.
export function reduceJobEvent(job, event, now = Date.now()) {
  if (!job || !event) {
    return { job, changed: false, needsResult: !!job && job.status === "completed" && !job.result };
  }
  const next = { ...job };
  let changed = false;
  let moved = false; // real progress → resets the stall clock
  const set = (key, value, isMove) => {
    if (value === undefined || sameValue(next[key], value)) return;
    next[key] = value;
    changed = true;
    if (isMove) moved = true;
  };

  const wasTerminal = TERMINAL.has(job.status);
  if (!wasTerminal) {
    const s = event.status;
    if (s === "completed" || s === "failed") set("status", s, true);
    else if (s === "unknown") set("status", "unknown", false);
    // The worker reports "pending" for every progress tick while the GET says
    // "processing" — never step a running job back to pending. A stalled
    // ("unknown") job is revived only by real movement (below), not by a poll
    // that merely repeats its status.
    else if (s === "processing" && job.status === "pending") set("status", "processing", true);

    if (isNum(event.progress)) set("progress", event.progress, true);
    if (event.stage) set("stage", event.stage, true);
    if (event.segment) set("segment", event.segment, true);
    if (next.status === "unknown" && moved) {
      next.status = "processing";
      changed = true;
    }
  }

  // The status GET answered 403/404 (gone, or not this account's job): stop
  // fetching it — a running job shows as "unknown". Only a later successful
  // GET (reachable) lifts the flag; socket movement alone proves nothing
  // about the GET.
  if (event.unreachable === true) {
    set("unreachable", true);
  } else if (event.reachable === true && job.unreachable) {
    set("unreachable", false);
    // "unknown" came from the failed GET, not from a stall: take the server's word.
    const s = event.status;
    if (!wasTerminal && next.status === "unknown" && (s === "pending" || s === "processing")) {
      set("status", s);
    }
  }

  if (next.status === "completed") {
    if (!wasTerminal) set("progress", 100);
    if (event.result && !job.result) set("result", event.result);
  }
  if (next.status === "failed" && event.error && (!wasTerminal || !job.error)) {
    set("error", event.error);
  }
  if (event.approved === true) set("approved", true);
  if (event.navId && !job.navId) set("navId", event.navId);

  if (!changed) {
    return { job, changed: false, needsResult: job.status === "completed" && !job.result };
  }
  next.updatedAt = now;
  if (moved || (next.status !== job.status && next.status !== "unknown")) next.lastChangeAt = now;
  return { job: next, changed: true, needsResult: next.status === "completed" && !next.result };
}

// ── Store ───────────────────────────────────────────────────────────────────
let currentTrainingId = null;
let jobs = {};
let hydrated = false;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      console.error("talkingAvatarJobStore listener error", e);
    }
  });
}

function readTrainingIdFromUrl() {
  try {
    return (
      new URLSearchParams(window.location.search).get("id") || window.idealStudioTrainingId || null
    );
  } catch (e) {
    return null;
  }
}

const storageKey = (tid) => `${STORAGE_PREFIX}${tid || "default"}`;

function load(tid) {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(storageKey(tid)) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
}

function persist() {
  try {
    sessionStorage.setItem(storageKey(currentTrainingId), JSON.stringify(jobs));
  } catch (e) {
    // sessionStorage full / unavailable — the in-memory store still works
  }
}

function prune(map, now) {
  const out = {};
  Object.keys(map || {}).forEach((id) => {
    const j = map[id];
    if (!j || !j.jobId) return;
    const t = Number(j.createdAt) || Number(j.updatedAt) || 0;
    if (!t || now - t > JOB_MAX_AGE_MS) return;
    out[id] = j;
  });
  return out;
}

export function hydrateTalkingAvatarJobs(trainingId = readTrainingIdFromUrl(), now = Date.now()) {
  currentTrainingId = trainingId || null;
  jobs = prune(load(currentTrainingId), now);
  hydrated = true;
  persist();
  emit();
  return jobs;
}

// Lazy, silent first load (safe inside a useSyncExternalStore snapshot read).
function ensureHydrated() {
  if (hydrated) return;
  currentTrainingId = readTrainingIdFromUrl();
  jobs = prune(load(currentTrainingId), Date.now());
  hydrated = true;
}

export function getTalkingAvatarJobs() {
  ensureHydrated();
  return jobs;
}

export function getStoredTalkingAvatarJob(jobId) {
  ensureHydrated();
  return (jobId && jobs[jobId]) || null;
}

export function upsertTalkingAvatarJob(record, now = Date.now()) {
  ensureHydrated();
  if (!record || !record.jobId) return null;
  const prev = jobs[record.jobId] || {};
  const next = {
    status: "pending",
    progress: 0,
    createdAt: now,
    ...prev,
    ...record,
    updatedAt: now,
  };
  if (!next.lastChangeAt) next.lastChangeAt = next.createdAt;
  jobs = { ...jobs, [record.jobId]: next };
  persist();
  emit();
  return next;
}

export function applyTalkingAvatarJobEvent(jobId, event, now = Date.now()) {
  ensureHydrated();
  const job = jobs[jobId];
  if (!job) return { job: null, changed: false, needsResult: false };
  const out = reduceJobEvent(job, event, now);
  if (out.changed) {
    jobs = { ...jobs, [jobId]: out.job };
    persist();
    emit();
  }
  return out;
}

function patchJob(jobId, patch) {
  ensureHydrated();
  if (!jobs[jobId]) return;
  jobs = { ...jobs, [jobId]: { ...jobs[jobId], ...patch } };
  persist();
  emit();
}

export function markTalkingAvatarJobApplied(jobId, now = Date.now()) {
  patchJob(jobId, { applied: true, appliedAt: now });
}

export function dismissTalkingAvatarJob(jobId) {
  patchJob(jobId, { dismissed: true });
}

// İş tepsisinde onaylanan / reddedilen / kaldırılan işler (jobSettled sinyali,
// JobWidgetHost iletir). Onay zaten sahneye uygular; ret/kaldır sonucu
// istemediğini söyler — her iki durumda da Avatar sekmesi o işi "Sahneye
// Ekle" ile yeniden sunmamalı.
export function settleTalkingAvatarJobs(jobIds) {
  ensureHydrated();
  const ids = (Array.isArray(jobIds) ? jobIds : []).filter((id) => jobs[id] && !jobs[id].settled);
  if (!ids.length) return;
  const next = { ...jobs };
  ids.forEach((id) => {
    next[id] = { ...next[id], settled: true };
  });
  jobs = next;
  persist();
  emit();
}

// Bekleyen sayılmaz: uygulanmış, gizlenmiş, tepsiden kapanmış ya da (başka
// sekmede / tepside) onaylanmış iş.
const isClosed = (j) => j.applied || j.dismissed || j.settled || j.approved === true;

const byNewest = (a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0);

// The job the Avatar tab shows for a scene: the newest running one, else the
// newest finished one the author has neither applied nor dismissed.
export function selectJobForScene(navId, map) {
  const all = map || getTalkingAvatarJobs();
  if (!navId) return null;
  const list = Object.values(all).filter((j) => j && j.navId === navId && !isClosed(j));
  if (!list.length) return null;
  const running = list.filter((j) => RUNNING.has(j.status));
  return (running.length ? running : list).sort(byNewest)[0];
}

// A still-running job for the scene, even one dismissed from view — a second
// submission for the same scene waits for it.
export function activeJobForScene(navId, map) {
  const all = map || getTalkingAvatarJobs();
  if (!navId) return null;
  const list = Object.values(all).filter((j) => j && j.navId === navId && RUNNING.has(j.status));
  return list.length ? list.sort(byNewest)[0] : null;
}

export function subscribeTalkingAvatarJobs(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useTalkingAvatarJobs() {
  return useSyncExternalStore(
    subscribeTalkingAvatarJobs,
    getTalkingAvatarJobs,
    getTalkingAvatarJobs
  );
}

// ── Narrow subscriptions ────────────────────────────────────────────────────
// useTalkingAvatarJobs() hands out the whole map, so its reader re-renders on
// every progress tick of ANY job. These subscribe to one slice instead.
// Records are immutable (every change replaces the object), so a slice that
// did not change keeps its reference and useSyncExternalStore skips the render.

// selector(jobs) must return a primitive or a record of the map as is — never
// a freshly built object (that would be a new snapshot on every read).
export function useTalkingAvatarJobsSelector(selector) {
  const getSnapshot = () => selector(getTalkingAvatarJobs());
  return useSyncExternalStore(subscribeTalkingAvatarJobs, getSnapshot, getSnapshot);
}

// The job the Avatar tab shows for one scene (selectJobForScene).
export function useSceneTalkingAvatarJob(navId) {
  return useTalkingAvatarJobsSelector((all) => selectJobForScene(navId, all));
}

// Scenes the sound modal marks with a dot: a job running or waiting to be added.
const marksScene = (j) => !!j && !!j.navId && !isClosed(j) && j.status !== "failed";
let navIdsCache = { jobs: null, key: "", value: new Set() };

// A Set that keeps its reference until the set of marked scenes changes.
export function getTalkingAvatarJobNavIds() {
  const all = getTalkingAvatarJobs();
  if (navIdsCache.jobs === all) return navIdsCache.value;
  const ids = [
    ...new Set(
      Object.values(all)
        .filter(marksScene)
        .map((j) => j.navId)
    ),
  ];
  const key = ids.map(String).sort().join("\n");
  navIdsCache =
    key === navIdsCache.key
      ? { ...navIdsCache, jobs: all }
      : { jobs: all, key, value: new Set(ids) };
  return navIdsCache.value;
}

export function useTalkingAvatarJobNavIds() {
  return useSyncExternalStore(
    subscribeTalkingAvatarJobs,
    getTalkingAvatarJobNavIds,
    getTalkingAvatarJobNavIds
  );
}

// ── Preferences (last model / voice), per browser ──────────────────────────
export function getTalkingAvatarPrefs() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
}

export function setTalkingAvatarPrefs(patch) {
  const next = { ...getTalkingAvatarPrefs(), ...(patch || {}) };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  } catch (e) {
    // ignore
  }
  return next;
}

// Test helper.
export function __resetTalkingAvatarJobStore() {
  jobs = {};
  hydrated = false;
  currentTrainingId = null;
  navIdsCache = { jobs: null, key: "", value: new Set() };
}
